import { ARApiError, ARLogger } from '@relayter/core';
import { ErrorFormatterUtil } from './error-formatter.util';
import { ENucToastType, NucToastDataModel, NucToastService } from '@relayter/rubber-duck';
import { ErrorConstants } from '../api/error.constants';

export enum TIME_OUT_TYPES {
    LONG = 10000
}

export class ToastOptionModel {
    public loading?: boolean = false;
    public timeout?: TIME_OUT_TYPES;
}

export class Toaster {
    private static toastService: NucToastService;
    private static readonly TOASTER_NOT_INITIALIZED_MSG = 'Toaster: Could not show toast: Toaster not initialized.';

    /**
     * Send out an error toast with an optional title
     */
    public static error(message: string, title?: string, options?: ToastOptionModel): void {
        if (Toaster.toastService) {
            title = title || 'Error';
            const toastData = new NucToastDataModel(ENucToastType.ERROR, title, message, options?.loading, options?.timeout);
            Toaster.toastService.show(toastData);
        } else {
            ARLogger.error(Toaster.TOASTER_NOT_INITIALIZED_MSG);
        }
    }

    /**
     * Handle an incoming ARApiError by mapping its error code to
     * the corresponding localized message and send out a warning toast
     * @param {ARApiError} error
     * @param {ToastOptionModel} [options]
     */
    public static handleApiWarning(error: any, options?: ToastOptionModel): void {
        if (Toaster.toastService) {
            const toastData = new NucToastDataModel(
                ENucToastType.WARNING,
                'Warning',
                ErrorFormatterUtil.formatError(error),
                options?.loading,
                options?.timeout
            );
            Toaster.toastService.show(toastData);
        } else {
            ARLogger.error(Toaster.TOASTER_NOT_INITIALIZED_MSG);
        }
    }

    /**
     * Handle an incoming ARApiError by showing the error details or mapping its error code to
     * the corresponding localized message and send out an error toast
     * @param {any} error
     */
    public static handleApiError(error: ARApiError): void {
        if (Toaster.toastService) {
            // Redirect HTTP error status 503 (Service Unavailable)
            if (
                [ErrorConstants.API_ERROR_CODES.SERVICE_UNAVAILABLE_MAINTENANCE, ErrorConstants.API_ERROR_CODES.SERVICE_UNAVAILABLE_DATABASE_TIMEOUT].includes(
                    error?.code
                )
            ) {
                Toaster.handleApiWarning(error, { timeout: TIME_OUT_TYPES.LONG });
            } else {
                Toaster.toastService.show(new NucToastDataModel(ENucToastType.ERROR, 'Error', ErrorFormatterUtil.formatError(error)));
            }
        } else {
            ARLogger.error(Toaster.TOASTER_NOT_INITIALIZED_MSG);
        }
    }

    /**
     * Send out a warning toast with an optional title
     */
    public static warn(message: string, title?: string, options?: ToastOptionModel): void {
        if (Toaster.toastService) {
            title = title || 'Warning';
            const toastData = new NucToastDataModel(ENucToastType.WARNING, title, message, options?.loading, options?.timeout);
            Toaster.toastService.show(toastData);
        } else {
            ARLogger.error(Toaster.TOASTER_NOT_INITIALIZED_MSG);
        }
    }

    /**
     * Send out an error toast for not yet implemented features
     */
    public static notYetImplementedError(): void {
        if (Toaster.toastService) {
            const toastData = new NucToastDataModel(ENucToastType.ERROR, 'Not yet implemented', 'TODO: This functionality has not yet been implemented');

            Toaster.toastService.show(toastData);
        } else {
            ARLogger.error(Toaster.TOASTER_NOT_INITIALIZED_MSG);
        }
    }

    /**
     * Send out a success toast with an optional title
     */
    public static success(message: string, title?: string, options?: ToastOptionModel): void {
        if (Toaster.toastService) {
            title = title || 'Success';
            const toastData = new NucToastDataModel(ENucToastType.SUCCESS, title, message, options?.loading, options?.timeout);
            Toaster.toastService.show(toastData);
        } else {
            ARLogger.error(Toaster.TOASTER_NOT_INITIALIZED_MSG);
        }
    }

    /**
     * General method to use toasts
     * @param toastData
     */
    public static openToast(toastData: NucToastDataModel): void {
        if (Toaster.toastService) {
            Toaster.toastService.show(toastData);
        } else {
            ARLogger.error(Toaster.TOASTER_NOT_INITIALIZED_MSG);
        }
    }

    /**
     * Initialize the Toaster
     */
    public static initialize(toastService: NucToastService): void {
        Toaster.toastService = toastService;
    }
}
